<script setup>
import { computed, inject, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  hasAiSuggestion,
  hasResolvedAiSuggestion,
} from 'dashboard/helper/aiSuggestionHelpers';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const showResolvedAiSuggestions = inject(
  'showResolvedAiSuggestions',
  ref(false)
);
const toggleResolvedAiSuggestions = () => {
  showResolvedAiSuggestions.value = !showResolvedAiSuggestions.value;
};

const canClearAiHistory = computed(() =>
  hasAiSuggestion(currentChat.value?.messages)
);
const canToggleResolvedAiSuggestions = computed(() =>
  hasResolvedAiSuggestion(currentChat.value?.messages)
);

watch(canToggleResolvedAiSuggestions, hasResolved => {
  if (!hasResolved) showResolvedAiSuggestions.value = false;
});

const clearAiHistoryDialogRef = ref(null);
const isClearingAiHistory = ref(false);

const openClearAiHistoryDialog = () => {
  clearAiHistoryDialogRef.value?.open();
};

const clearAiHistory = async () => {
  if (isClearingAiHistory.value) return;
  isClearingAiHistory.value = true;
  try {
    const data = await store.dispatch('clearAiHistory', {
      conversationId: currentChat.value.id,
    });
    const isEmpty =
      data?.messages_deleted === 0 && data?.suggestions_deleted === 0;
    if (isEmpty) {
      useAlert(t('CONVERSATION.CLEAR_AI_HISTORY.SUCCESS_EMPTY'));
    } else {
      useAlert(t('CONVERSATION.CLEAR_AI_HISTORY.SUCCESS'));
    }
  } catch (error) {
    const apiError = error?.response?.data?.error;
    const notLinked =
      typeof apiError === 'string' && apiError.includes('not linked');
    if (notLinked) {
      useAlert(t('CONVERSATION.CLEAR_AI_HISTORY.ERROR_NOT_LINKED'));
    } else {
      useAlert(t('CONVERSATION.CLEAR_AI_HISTORY.ERROR'));
    }
  } finally {
    isClearingAiHistory.value = false;
    clearAiHistoryDialogRef.value?.close();
  }
};

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <ButtonV4
        v-if="canClearAiHistory"
        v-tooltip="$t('CONVERSATION.CLEAR_AI_HISTORY.BUTTON')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-eraser"
        class="rounded-md"
        :is-loading="isClearingAiHistory"
        :disabled="isClearingAiHistory"
        @click="openClearAiHistoryDialog"
      />
      <ButtonV4
        v-if="canToggleResolvedAiSuggestions"
        v-tooltip="
          showResolvedAiSuggestions
            ? $t('CONVERSATION.HEADER.HIDE_AI_SUGGESTIONS')
            : $t('CONVERSATION.HEADER.SHOW_AI_SUGGESTIONS')
        "
        size="sm"
        :variant="showResolvedAiSuggestions ? 'faded' : 'ghost'"
        color="slate"
        icon="i-lucide-sparkles"
        class="rounded-md"
        @click="toggleResolvedAiSuggestions"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
    <Dialog
      ref="clearAiHistoryDialogRef"
      type="alert"
      :title="t('CONVERSATION.CLEAR_AI_HISTORY.TITLE')"
      :description="t('CONVERSATION.CLEAR_AI_HISTORY.DESCRIPTION')"
      :confirm-button-label="t('CONVERSATION.CLEAR_AI_HISTORY.CONFIRM')"
      :cancel-button-label="t('CONVERSATION.CLEAR_AI_HISTORY.CANCEL')"
      :is-loading="isClearingAiHistory"
      @confirm="clearAiHistory"
    />
  </div>
</template>
